import { useCallback, useEffect, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Trash2, Save, PackageCheck, Search, Plus, X,
  BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { adminApi, useAdminAuth } from "../lib/adminAuth";
import FdaLabelPanel from "../components/admin/FdaLabelPanel";
import SourceChips from "../components/admin/SourceChips";

function MedChips({ meds, names, allMeds, onChange }) {
  const [adding, setAdding] = useState("");
  const available = allMeds.filter((m) => !meds.includes(m.id));
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {meds.map((mid) => (
        <span key={mid} className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700">
          {names[mid] || mid}
          <button type="button" onClick={() => onChange(meds.filter((x) => x !== mid))}
            className="text-stone-400 hover:text-red-600" aria-label="remove">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <Select value={adding} onValueChange={(v) => { onChange([...meds, v]); setAdding(""); }}>
        <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="+ link medication" /></SelectTrigger>
        <SelectContent className="max-h-72">
          {available.map((m) => (
            <SelectItem key={m.id} value={m.id} className="text-xs">{m.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function AltRow({ alt, allMeds, onSaved, onRemoved, canReview }) {
  const [meds, setMeds] = useState(alt.safe_with_meds || []);
  const [verified, setVerified] = useState(alt.verified);
  const [why, setWhy] = useState(alt.why_safer || "");
  const [sources, setSources] = useState(alt.sources || []);
  const [reviewedBy, setReviewedBy] = useState(alt.reviewed_by || "");
  const [buyUrl, setBuyUrl] = useState(alt.buy_url || "");
  const [retailerName, setRetailerName] = useState(alt.retailer_name || "");
  const [busy, setBusy] = useState("");
  const [showPanel, setShowPanel] = useState(false);

  const names = Object.fromEntries(allMeds.map((m) => [m.id, m.label]));

  async function save() {
    setBusy("save");
    try {
      const { data } = await adminApi().put(`/admin/alternatives/${alt.id}`, {
        is_safe_alternative: true,
        verified,
        safe_with_meds: meds,
        why_safer: why || null,
        sources,
        reviewed_by: reviewedBy || null,
        buy_url: buyUrl || null,
        retailer_name: retailerName || null,
      });
      onSaved(data);
      toast.success(data.verified ? "Saved — live for linked medications" : "Saved as draft (not yet user-visible)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  }

  async function remove() {
    if (!window.confirm("Remove this product from the alternatives list?")) return;
    setBusy("delete");
    try {
      await adminApi().delete(`/admin/alternatives/${alt.id}`);
      onRemoved(alt.id);
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
      setBusy("");
    }
  }

  return (
    <div data-testid={`alt-row-${alt.id}`}
      className={`rounded-xl border p-4 ${verified ? "border-emerald-300 bg-emerald-50/40" : "border-stone-200 bg-white"}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-stone-900">{alt.full_name}</h3>
            {verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-800">
                <BadgeCheck className="h-3 w-3" /> Live
              </span>
            )}
          </div>
          <p className="text-xs text-stone-500">{alt.brand} · {alt.category}</p>
        </div>
        <Button data-testid={`alt-delete-${alt.id}`} size="sm" variant="ghost"
          className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={remove}
          disabled={!!busy || !canReview}
          title={!canReview ? "Clinical reviewer only." : undefined}>
          {busy === "delete" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </Button>
      </div>

      <div className="mt-3 space-y-3">
        <div>
          <label className="text-[11px] font-medium uppercase tracking-wide text-stone-500">For medications</label>
          <div className="mt-1"><MedChips meds={meds} names={names} allMeds={allMeds} onChange={setMeds} /></div>
        </div>
        <Textarea data-testid={`alt-why-${alt.id}`} value={why} onChange={(e) => setWhy(e.target.value)}
          rows={2} className="text-sm" placeholder="Why it's safer (e.g. fragrance-free ceramide moisturizer, no actives that clash)" />
        <div>
          <label className="text-[11px] font-medium uppercase tracking-wide text-stone-500">Sources</label>
          <div className="mt-1">
            <SourceChips sources={sources} testid={`alt-sources-${alt.id}`} onChange={setSources} />
          </div>
        </div>
        <Input data-testid={`alt-reviewer-${alt.id}`} value={reviewedBy} onChange={(e) => setReviewedBy(e.target.value)}
          className="text-xs" placeholder="Reviewed by (e.g. PharmD student)" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-stone-500">Where to buy — link (optional)</label>
            <Input data-testid={`alt-buy-url-${alt.id}`} value={buyUrl} onChange={(e) => setBuyUrl(e.target.value)}
              className="mt-1 text-xs" placeholder="https://… (affiliate ok)" />
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-stone-500">Retailer name (optional)</label>
            <Input data-testid={`alt-retailer-${alt.id}`} value={retailerName} onChange={(e) => setRetailerName(e.target.value)}
              className="mt-1 text-xs" placeholder="e.g. Amazon, Sephora" />
          </div>
        </div>
        <p className="text-[10.5px] leading-snug text-stone-400">
          Add a buy link only when you want it shown. When present, the Discussion Report adds a
          “Where to buy” line and the required commission disclosure automatically.
        </p>
        <label className="flex items-center gap-2 text-xs text-stone-700">
          <input data-testid={`alt-verified-${alt.id}`} type="checkbox" checked={verified}
            disabled={!canReview}
            onChange={(e) => setVerified(e.target.checked)} className="h-4 w-4 rounded border-stone-300" />
          Verified — show to users (needs ≥1 medication, a source + a reviewer)
        </label>
        <div className="flex items-center gap-2">
          <Button data-testid={`alt-save-${alt.id}`} size="sm" onClick={save}
            disabled={!!busy || !canReview}
            title={!canReview ? "Clinical reviewer only." : undefined}>
            {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            <span className="ml-1">Save</span>
          </Button>
          {!canReview && (
            <span
              data-testid={`alt-clinician-only-${alt.id}`}
              className="text-[11px] font-semibold uppercase tracking-wider text-stone-500"
            >
              Clinical reviewer only
            </span>
          )}
        </div>

        <div className="border-t border-stone-100 pt-2">
          {meds.length === 0 ? (
            <p className="text-[12px] text-stone-400">Link a medication above to cite from its FDA label.</p>
          ) : (
            <button
              className="text-[12px] font-medium text-stone-600 underline-offset-2 hover:text-stone-900 hover:underline"
              onClick={() => setShowPanel((s) => !s)}
              data-testid={`alt-cite-toggle-${alt.id}`}
            >
              {showPanel ? "Hide FDA label" : `Cite from FDA label (${names[meds[0]] || meds[0]})`}
            </button>
          )}
          {showPanel && meds.length > 0 && (
            <div className="mt-2">
              <FdaLabelPanel
                medicationId={meds[0]}
                medicationName={names[meds[0]] || meds[0]}
                useHint="this alternative's sources"
                onUse={(src) => {
                  if (sources.some((x) => x.url === src.url)) return false;
                  setSources((cur) => (cur.some((x) => x.url === src.url)
                    ? cur : [...cur, { url: src.url, label: src.label }]));
                  return true;
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductSearch({ existingIds, onAdded, canReview }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  async function search(e) {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await adminApi().get(`/admin/alternatives/product-search?q=${encodeURIComponent(q.trim())}`);
      setResults(data.items);
    } catch (e2) {
      toast.error("Search failed");
    } finally { setBusy(false); }
  }

  async function add(p) {
    try {
      const { data } = await adminApi().put(`/admin/alternatives/${p.id}`, {
        is_safe_alternative: true, verified: false, safe_with_meds: [],
      });
      onAdded(data);
      setResults((r) => r.filter((x) => x.id !== p.id));
      toast.success("Added as draft — link a medication and verify to publish");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  }

  return (
    <div data-testid="alt-add-panel" className="rounded-xl border border-dashed border-stone-300 bg-white p-4">
      <form onSubmit={search} className="flex gap-2">
        <Input data-testid="alt-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search products to add (name or brand)…" className="text-sm" />
        <Button data-testid="alt-search-btn" type="submit" variant="outline" disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </form>
      {results.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {results.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded-lg border border-stone-100 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-stone-800">{p.full_name || p.name}</p>
                <p className="text-xs text-stone-500">{p.brand} · {p.category}</p>
              </div>
              {existingIds.includes(p.id) ? (
                <span className="text-xs text-stone-400">Already added</span>
              ) : (
                <Button size="sm" variant="outline" onClick={() => add(p)} data-testid={`alt-add-${p.id}`}
                  disabled={!canReview} title={!canReview ? "Clinical reviewer only." : undefined}>
                  <Plus className="h-3.5 w-3.5" /> <span className="ml-1">Add</span>
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAlternatives() {
  const { admin } = useAdminAuth();
  const canReview = admin?.role === "clinician";
  const [items, setItems] = useState([]);
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        adminApi().get("/admin/alternatives"),
        adminApi().get("/admin/alternatives/medications"),
      ]);
      setItems(a.data.items);
      setMeds(m.data.items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load alternatives");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function onSaved(updated) {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
  }
  function onRemoved(id) { setItems((prev) => prev.filter((i) => i.id !== id)); }
  function onAdded(created) {
    setItems((prev) => (prev.some((i) => i.id === created.id) ? prev : [created, ...prev]));
  }

  const verifiedCount = items.filter((i) => i.verified).length;

  return (
    <main data-testid="admin-alternatives-page" className="min-h-screen bg-[#fdf8f3] px-6 py-10">
      <div className="mx-auto max-w-3xl">
        <AdminHeader />

        <div className="flex items-center gap-2">
          <PackageCheck className="h-6 w-6 text-stone-700" />
          <h1 className="text-3xl font-semibold tracking-tight text-stone-900">What to use instead</h1>
        </div>
        <p className="mt-1 text-sm text-stone-600">
          These are the “What to use instead” products shown on a RED/CAUTION
          check and in the Discussion Report. A product only appears to a user
          once it’s <strong>verified</strong> and linked to the medication(s)
          it’s been cleared against.
        </p>

        <div className="mt-4 rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-stone-600">
          <span data-testid="alt-count"><strong className="text-stone-900">{verifiedCount}</strong> live · {items.length} total</span>
          {!canReview && (
            <span data-testid="alt-clinician-note" className="ml-2 text-stone-500">
              · Clinical reviewer only — Meghana verifies and publishes alternatives.
            </span>
          )}
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>
        ) : (
          <div className="mt-5 space-y-3">
            <ProductSearch existingIds={items.map((i) => i.id)} onAdded={onAdded} canReview={canReview} />
            {items.map((alt) => (
              <AltRow key={alt.id} alt={alt} allMeds={meds} onSaved={onSaved} onRemoved={onRemoved} canReview={canReview} />
            ))}
            {items.length === 0 && (
              <p className="py-8 text-center text-sm text-stone-500">
                No alternatives yet. Search above to add your first product.
              </p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
